import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
const io = require('socket.io-client/dist/socket.io.js');

// Logi diagnostyczne tylko na środowisku deweloperskim. Świadomie NIE opieramy się
// na environment.production — environment.prod.ts bywa przełączany na API dev
// (ta sama logika co w app.component.ts), więc o środowisku decyduje adres API.
// Bez tego każdy event socketu (a przy alarmach lecą ciągle) logował się na produkcji.
const DEBUG = environment.api_url.includes('sidly-platform-dev');

@Injectable()
export class SocketService {

  private readonly socketUrl = environment.socket_url;
  private readonly LOCAL_URL = 'http://127.0.0.1:3130';

  private socket: any;
  private turnOffAlarm = true;

  // Rejestr aktywnych listenerów. fromEvent() dopisuje tutaj, a init() po każdym
  // (re)utworzeniu socketa przepina wszystkie na nową instancję. Wcześniej handler
  // wiązał się z instancją socketa istniejącą W MOMENCIE subskrypcji — komponent,
  // który zasubskrybował przed init() (albo przetrwał re-login, gdy init() wymienia
  // socket), nigdy nie dostawał eventów (bug: sidebar nie widział workstationChange).
  private listeners: Array<{ event: string; handler: (data: any) => void }> = [];

  public init(userId: string | number): void {
    if (this.socket) {
      if (DEBUG) { console.log('[SocketService] init: disconnecting previous socket'); }
      this.socket.disconnect();
      this.socket = null;
    }

    const options = this.socketUrl === this.LOCAL_URL
      ? { path: '/socket.io/', query: `userId=${userId}&` }
      : { path: `/socket2/socket.io/?userId=${userId}&` };

    if (DEBUG) { console.log('[SocketService] init: connecting', { url: this.socketUrl, userId: userId, options: options }); }
    this.socket = this.createSocket(this.socketUrl, options);

    this.socket.on('connect', () => {
      if (DEBUG) { console.log('[SocketService] connected, socket.id =', this.socket && this.socket.id); }
    });
    this.socket.on('disconnect', (reason: any) => {
      console.warn('[SocketService] disconnected:', reason);
    });
    this.socket.on('connect_error', (err: any) => {
      console.error('[SocketService] connect_error:', err && (err.message || err));
    });
    this.socket.on('reconnect', (attempt: any) => {
      if (DEBUG) { console.log('[SocketService] reconnected after', attempt, 'attempts'); }
    });

    // Przepnij wszystkie żywe subskrypcje na nowy socket
    this.listeners.forEach(l => {
      this.socket.on(l.event, l.handler);
    });
    if (DEBUG && this.listeners.length) {
      console.log('[SocketService] re-attached listeners:', this.listeners.map(l => l.event));
    }
  }

  private createSocket(url: string, options: any): any {
    return io(url, options);
  }

  public close(): void {
    if (this.socket) {
      if (DEBUG) { console.log('[SocketService] close()'); }
      this.socket.disconnect();
      this.socket = null;
    }
  }

  private fromEvent<T = any>(eventName: string): Observable<T> {
    return new Observable<T>(observer => {
      const handler = (data: any) => {
        if (DEBUG) {
          const preview = typeof data === 'string' ? data.substring(0, 300) : data;
          console.log(`[SocketService] event '${eventName}':`, preview);
        }
        try {
          observer.next(typeof data === 'string' ? JSON.parse(data) : data);
        } catch (e) {
          console.error(`[SocketService] Error parsing data for event ${eventName}:`, e);
          observer.next(data);
        }
      };

      const entry = { event: eventName, handler: handler };
      this.listeners.push(entry);
      if (DEBUG) {
        console.log(`[SocketService] listen '${eventName}' (socket ${this.socket ? 'ready' : 'NOT READY yet — attach at init'})`);
      }

      if (this.socket) {
        this.socket.on(eventName, handler);
      }

      return () => {
        const idx = this.listeners.indexOf(entry);
        if (idx !== -1) {
          this.listeners.splice(idx, 1);
        }
        if (this.socket) {
          this.socket.off(eventName, handler);
        }
      };
    });
  }

  private emitEvent(eventName: string, data: any): void {
    if (!this.socket) {
      return;
    }
    this.socket.emit(eventName, data);
  }

  public onAlert(): Observable<any>    { return this.fromEvent('sosAlert'); }
  public onAlertCPL(): Observable<any> { return this.fromEvent('cplinfo'); }

  public onAlertEBS(): Observable<any> {
    return this.turnOffAlarm ? this.fromEvent('sensorAlarm') : new Observable(o => o.complete());
  }

  public startCare(): Observable<any>     { return this.fromEvent('startCare'); }
  public stopCare(): Observable<any>      { return this.fromEvent('stopCare'); }
  public startUsing(): Observable<any>    { return this.fromEvent('startUsing'); }
  public stopUsing(): Observable<any>     { return this.fromEvent('stopUsing'); }
  public updateOfData(): Observable<any>  { return this.fromEvent('updateOfData'); }
  public startCharging(): Observable<any> { return this.fromEvent('startCharge'); }
  /**
   * Backend rozroznia dwa zdarzenia ladowania: 'startCharge' (opaska.charging(), MEAS
   * 'Band charging'/'Band fullcharged') i 'onCharger' (opaska.onCharger(), MEAS 'Band on
   * charger' — typowy przypadek: zdjeta i polozona na ladowarce). Do niedawna front
   * nasluchiwal tylko pierwszego, wiec zywe listy nigdy nie lapaly tego drugiego zdarzenia.
   */
  public onCharger(): Observable<any>     { return this.fromEvent('onCharger'); }

  // Czat opiekunów — nowe wiadomości i potwierdzenia przeczytania.
  public onChatMessage(): Observable<any>        { return this.fromEvent('chat:message'); }
  public onChatRead(): Observable<any>           { return this.fromEvent('chat:read'); }
  public onChatTyping(): Observable<any>         { return this.fromEvent('chat:typing'); }

  /** „Pisze..." — emit do node, który przekazuje rozmówcy (best-effort). */
  public emitChatTyping(peerId: any): void {
    this.emitEvent('chat:typing', JSON.stringify({ peerId }));
  }

  public onNotification(): Observable<any>       { return this.fromEvent('informationNotification'); }
  public onAccept(): Observable<any>             { return this.fromEvent('canceling'); }
  public onStatus(): Observable<any>             { return this.fromEvent('statusOfCaregivers'); }
  public handleSideNotif(): Observable<any>      { return this.fromEvent('handleSideNotif'); }
  public startHandleSideNotif(): Observable<any> { return this.fromEvent('startHandleSideNotif'); }
  public refreshOnAlarmHandle(): Observable<any> { return this.fromEvent('refresh-page'); }
  public snackbarAH(): Observable<any>           { return this.fromEvent('snackbarAH'); }
  public snackbar(): Observable<any>             { return this.fromEvent('snackbar'); }
  public sendSignal(): Observable<any>           { return this.fromEvent('sendSignal'); }

  public onIncomingSms(): Observable<any>       { return this.fromEvent('incomingSms'); }
  public onWorkstationChange(): Observable<any> { return this.fromEvent('workstationChange'); }
  public onCallStateChange(): Observable<any>   { return this.fromEvent('callStateChange'); }

  public acceptRequest(data: any): void { this.emitEvent('canceling', data); }
  public checkAlarms(): void            { this.emitEvent('checkAlarm', null); }
}

/**
 * Czy 'canceling' dotyczy tego alarmu. Ekran obsługi otwarty z widgetu telefonu (alarm z połączenia)
 * wysyła obiekt bez `id` pacjenta — tylko `user_id` i `alarmID` — więc porównanie po samym `id`
 * zostawiało okno alarmu u pozostałych ratowników.
 */
export function isCancelingFor(data: any, alarm: any): boolean {
  if (!data || !alarm) {
    return false;
  }
  if (data.alarmID != null && alarm.alarmID != null) {
    return String(data.alarmID) === String(alarm.alarmID);
  }
  return data.id != null && data.id === alarm.id;
}
